import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FavoriteCenters = ({ user, onDataChange }) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'rating', 'distance'

  // Mock favorite centers data - in real app this would come from API
  const favoriteCenters = [
    {
      id: 1,
      name: 'Harmony Wellness Center',
      type: 'EES Center',
      location: 'Los Angeles, CA',
      distance: '2.3 miles',
      rating: 4.9,
      reviewCount: 156,
      image: '/assets/images/wellness_center_1.jpg',
      specialties: ['EES Therapy', 'Meditation', 'Sound Healing'],
      priceRange: '$80-150',
      nextAvailable: '2024-01-25',
      isFeatured: true,
      description: 'Premier wellness destination offering state-of-the-art EES technology in a serene environment.',
      amenities: ['Parking', 'WiFi', 'Refreshments', 'Meditation Garden'],
      lastVisited: '2024-01-20',
      totalSessions: 8
    },
    {
      id: 2,
      name: 'Zenith Retreat Center',
      type: 'Meditation Retreat',
      location: 'San Diego, CA',
      distance: '45.2 miles',
      rating: 4.7,
      reviewCount: 89,
      image: '/assets/images/wellness_center_2.jpg',
      specialties: ['Mindfulness', 'Yoga', 'Breathwork'],
      priceRange: '$60-120',
      nextAvailable: '2024-01-28',
      isFeatured: false,
      description: 'Tranquil retreat center specializing in traditional meditation practices and spiritual growth.',
      amenities: ['Organic Meals', 'Nature Trails', 'Library', 'Yoga Studio'],
      lastVisited: '2024-01-18',
      totalSessions: 3
    },
    {
      id: 3,
      name: 'Sacred Sound Studio',
      type: 'Sound Healing',
      location: 'Santa Monica, CA',
      distance: '8.7 miles',
      rating: 4.8,
      reviewCount: 124,
      image: '/assets/images/wellness_center_3.jpg',
      specialties: ['Crystal Bowls', 'Tibetan Singing Bowls', 'Gong Therapy'],
      priceRange: '$75-110',
      nextAvailable: '2024-01-26',
      isFeatured: true,
      description: 'Immersive sound healing sanctuary with ancient and modern therapeutic instruments.',
      amenities: ['Sound-proof Rooms', 'Comfortable Seating', 'Tea Service', 'Crystals'],
      lastVisited: '2024-01-15',
      totalSessions: 5
    },
    {
      id: 4,
      name: 'Quantum Healing Center',
      type: 'Holistic Wellness',
      location: 'Beverly Hills, CA',
      distance: '15.1 miles',
      rating: 4.6,
      reviewCount: 203,
      image: '/assets/images/wellness_center_4.jpg',
      specialties: ['EES Therapy', 'Reiki', 'Chakra Balancing'],
      priceRange: '$100-200',
      nextAvailable: '2024-01-22',
      isFeatured: false,
      description: 'Luxury wellness center combining cutting-edge technology with traditional healing arts.',
      amenities: ['Valet Parking', 'Spa Services', 'Private Rooms', 'Healthy Cafe'],
      lastVisited: null,
      totalSessions: 0
    }
  ];

  const sortedCenters = [...favoriteCenters].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'distance':
        return parseFloat(a.distance) - parseFloat(b.distance);
      case 'recent':
      default:
        if (!a.lastVisited) return 1;
        if (!b.lastVisited) return -1;
        return new Date(b.lastVisited) - new Date(a.lastVisited);
    }
  });

  const handleRemoveFavorite = (centerId) => {
    // In real app, this would make API call to remove from favorites
    console.log('Removing from favorites:', centerId);
    onDataChange?.();
  };

  const handleBookSession = (center) => {
    // In real app, this would navigate to booking page
    console.log('Booking session at:', center.name);
  };

  const handleShareCenter = (center) => {
    // In real app, this would open share dialog
    if (navigator.share) {
      navigator.share({
        title: center.name,
        text: `Check out ${center.name} - ${center.description}`,
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(`${center.name} - ${window.location.href}`);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Icon
        key={i}
        name="Star"
        size={14}
        className={i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-slate-300'}
      />
    ));
  };

  const CenterCard = ({ center }) => (
    <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100 overflow-hidden hover:shadow-xl transition-all duration-300">
      {/* Center Image */}
      <div className="relative h-48">
        <img
          src={center.image}
          alt={center.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = '/assets/images/no_image.png';
          }}
        />
        
        <div className="absolute top-3 right-3 flex space-x-2">
          {center.isFeatured && (
            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
              Featured
            </span>
          )}
          
          <button
            onClick={() => handleRemoveFavorite(center.id)}
            className="bg-white/90 hover:bg-red-50 p-2 rounded-full transition-colors duration-200"
            title="Remove from favorites"
          >
            <Icon name="Heart" size={16} className="text-red-500 fill-current" />
          </button>
        </div>
        
        <div className="absolute bottom-3 left-3">
          <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium">
            {center.type}
          </span>
        </div>
      </div>
      
      {/* Center Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">{center.name}</h3>
          <div className="flex items-center space-x-1">
            <div className="flex space-x-1">
              {renderStars(center.rating)}
            </div>
            <span className="text-sm text-slate-600 ml-1">
              {center.rating} ({center.reviewCount})
            </span>
          </div>
        </div>
        
        <p className="text-sm text-slate-600 mb-3 line-clamp-2">{center.description}</p>
        
        <div className="space-y-2 text-sm text-slate-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={14} />
              <span>{center.location}</span>
            </div>
            <span>{center.distance}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <Icon name="DollarSign" size={14} />
              <span>{center.priceRange}</span>
            </div>
            <span>Next: {new Date(center.nextAvailable).toLocaleDateString()}</span>
          </div>
        </div>
        
        {/* Specialties */}
        <div className="mt-3">
          <div className="flex flex-wrap gap-1">
            {center.specialties.slice(0, 3).map((specialty, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                {specialty}
              </span>
            ))}
            {center.specialties.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600">
                +{center.specialties.length - 3} more
              </span>
            )}
          </div>
        </div>
        
        {/* Visit History */}
        {center.totalSessions > 0 && (
          <div className="mt-3 p-2 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between text-xs text-blue-800">
              <span>You've visited {center.totalSessions} times</span>
              <span>Last: {new Date(center.lastVisited).toLocaleDateString()}</span>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="mt-4 flex space-x-2">
          <button
            onClick={() => handleBookSession(center)}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            Book Session
          </button>
          
          <button
            onClick={() => handleShareCenter(center)}
            className="flex items-center justify-center px-3 py-2 border border-blue-200 text-blue-600 rounded-md hover:bg-blue-50 transition-colors duration-200"
          >
            <Icon name="Share" size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const CenterListItem = ({ center }) => (
    <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
      <div className="flex items-start space-x-4">
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={center.image}
            alt={center.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/assets/images/no_image.png';
            }}
          />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">{center.name}</h3>
              <div className="flex items-center space-x-2 mb-2">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  {center.type}
                </span>
                {center.isFeatured && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                    Featured
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="flex space-x-1">
                  {renderStars(center.rating)}
                </div>
                <span className="text-sm text-slate-600">
                  {center.rating} ({center.reviewCount})
                </span>
              </div>
              
              <button
                onClick={() => handleRemoveFavorite(center.id)}
                className="p-2 hover:bg-red-50 rounded-full transition-colors duration-200"
                title="Remove from favorites"
              >
                <Icon name="Heart" size={16} className="text-red-500 fill-current" />
              </button>
            </div>
          </div>
          
          <p className="text-sm text-slate-600 mb-3">{center.description}</p>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-slate-600 mb-3">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={14} />
              <span>{center.location} • {center.distance}</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <Icon name="DollarSign" size={14} />
              <span>{center.priceRange}</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>Next: {new Date(center.nextAvailable).toLocaleDateString()}</span>
            </div>
            
            {center.totalSessions > 0 && (
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={14} />
                <span>{center.totalSessions} visits</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {center.specialties.slice(0, 4).map((specialty, index) => (
                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {specialty}
                </span>
              ))}
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => handleShareCenter(center)}
                className="flex items-center justify-center px-3 py-2 border border-blue-200 text-blue-600 rounded-md hover:bg-blue-50 transition-colors duration-200"
              >
                <Icon name="Share" size={16} />
              </button>
              
              <button
                onClick={() => handleBookSession(center)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Book Session
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
              <Icon name="Heart" size={20} className="mr-2 text-red-500" />
              Favorite Wellness Centers
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Your saved wellness destinations for easy booking and sharing
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-blue-200 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="recent">Recent Visits</option>
              <option value="rating">Highest Rated</option>
              <option value="distance">Nearest</option>
            </select>
            
            <div className="flex border border-blue-200 rounded-md">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors duration-200 ${
                  viewMode === 'grid' ?'bg-blue-600 text-white' :'text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Icon name="Grid3X3" size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors duration-200 ${
                  viewMode === 'list' ?'bg-blue-600 text-white' :'text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Icon name="List" size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Centers Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCenters.map((center) => (
            <CenterCard key={center.id} center={center} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedCenters.map((center) => (
            <CenterListItem key={center.id} center={center} />
          ))}
        </div>
      )}

      {favoriteCenters.length === 0 && (
        <div className="text-center py-12 bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <Icon name="Heart" size={48} className="text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No favorite centers yet</h3>
          <p className="text-slate-500 mb-4">
            Start exploring wellness centers and save your favorites for easy access
          </p>
          <button className="bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200">
            Explore Wellness Centers
          </button>
        </div>
      )}
    </div>
  );
};

export default FavoriteCenters;